import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarClock, Layers, PiggyBank, Wallet } from 'lucide-react';
import { useInvestments, useSummary } from '@/api/hooks';
import { formatMonthRange, formatMoneyShort } from '@/lib/format';
import { UNGROUPED_COLOR } from '@/lib/palette';
import { Card, CardHeader } from '@/components/ui/Card';
import { StatTile } from '@/components/charts/StatTile';
import { BudgetMeter } from '@/components/charts/BudgetMeter';
import { GroupCostBars } from '@/components/charts/GroupCostBars';
import { MonthlyColumns } from '@/components/charts/MonthlyColumns';
import { StatusBadge, GroupBadge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader, QueryState } from '@/components/PageHeader';

export function Dashboard() {
  const navigate = useNavigate();
  const summary = useSummary();
  const investments = useInvestments();

  const upcoming = useMemo(() => {
    const items = (investments.data ?? []).filter(
      (investment) => investment.plannedFrom !== null && investment.status !== 'cancelled',
    );
    return items.sort((a, b) => a.plannedFrom!.localeCompare(b.plannedFrom!)).slice(0, 5);
  }, [investments.data]);

  return (
    <>
      <PageHeader
        title="Übersicht"
        subtitle="Was das Haus insgesamt kosten soll – und wie es sich verteilt."
      />

      <QueryState
        isLoading={summary.isLoading}
        error={(summary.error as Error | null) ?? null}
      >
        {summary.data ? (
          <div className="flex flex-col gap-5">
            <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <StatTile
                hero
                label="Geplante Gesamtkosten"
                value={formatMoneyShort(summary.data.totals.effective, summary.data.currency)}
                hint={`${summary.data.totals.investmentCount} Investitionen in ${summary.data.totals.groupCount} Gruppen`}
                icon={<Wallet size={16} />}
              />
              <StatTile
                label="Budgetrahmen"
                value={
                  summary.data.budget.periods.length === 0
                    ? 'Nicht gesetzt'
                    : formatMoneyShort(summary.data.budget.total, summary.data.currency)
                }
                hint={
                  summary.data.budget.periods.length === 0
                    ? 'Perioden unter „Budget" anlegen'
                    : `Summe aus ${summary.data.budget.periods.length} Periode${
                        summary.data.budget.periods.length === 1 ? '' : 'n'
                      }`
                }
                icon={<PiggyBank size={16} />}
              />
              <StatTile
                label={summary.data.budget.remaining < 0 ? 'Über dem Rahmen' : 'Im Rahmen frei'}
                value={
                  summary.data.budget.periods.length === 0
                    ? '–'
                    : formatMoneyShort(
                        Math.abs(summary.data.budget.remaining),
                        summary.data.currency,
                      )
                }
                tone={
                  summary.data.budget.periods.length === 0
                    ? 'neutral'
                    : summary.data.budget.remaining < 0
                      ? 'critical'
                      : 'good'
                }
                hint={
                  summary.data.budget.periods.length === 0
                    ? 'Budget je Zeitraum setzen'
                    : summary.data.budget.remaining < 0
                      ? 'Planung liegt über dem Rahmen'
                      : 'Noch nicht verplant'
                }
                icon={<PiggyBank size={16} />}
              />
              <StatTile
                label="Noch ohne Termin"
                value={formatMoneyShort(summary.data.unscheduled.total, summary.data.currency)}
                hint={`${summary.data.unscheduled.investmentCount} Investition(en) ohne Datum`}
                icon={<CalendarClock size={16} />}
              />
            </section>

            <div className="grid gap-5 lg:grid-cols-[1.1fr_1fr]">
              <Card>
                <CardHeader
                  title="Kosten je Gruppe"
                  subtitle="Positionen, wo erfasst – sonst die Schätzung"
                />
                <div className="px-5 pb-5">
                  {summary.data.byGroup.length > 0 ? (
                    <GroupCostBars
                      currency={summary.data.currency}
                      onSelect={(key) => navigate(`/projekte?gruppe=${encodeURIComponent(key)}`)}
                      data={summary.data.byGroup.map((bucket) => ({
                        key: bucket.groupId ?? 'ungrouped',
                        label: bucket.name,
                        color: bucket.groupId === null ? UNGROUPED_COLOR : bucket.color,
                        value: bucket.total,
                        count: bucket.investmentCount,
                      }))}
                    />
                  ) : (
                    <EmptyState
                      icon={<Layers size={22} />}
                      title="Noch keine Gruppen"
                      description="Lege unter „Projekte“ eine Gruppe wie „Terrasse“ an."
                    />
                  )}
                </div>
              </Card>

              <div className="flex flex-col gap-5">
                <Card>
                  <CardHeader
                    title="Budget je Zeitraum"
                    subtitle="Auslastung der einzelnen Perioden"
                    action={
                      <Link
                        to="/budget"
                        className="text-xs text-[var(--accent)] underline-offset-2 hover:underline"
                      >
                        Budget
                      </Link>
                    }
                  />
                  <div className="flex flex-col gap-4 px-5 pb-5">
                    {summary.data.budget.periods.length === 0 ? (
                      <p className="text-xs text-[var(--ink-3)]">
                        Noch kein Budget je Zeitraum. Lege unter „Budget" eine Periode an –
                        üblicherweise ein Kalenderjahr.
                      </p>
                    ) : (
                      summary.data.budget.periods.map((period) => (
                        <div key={period.id} className="flex flex-col gap-1.5">
                          <div className="flex items-baseline justify-between gap-2">
                            <Link
                              to="/budget"
                              className="text-sm font-medium hover:text-[var(--accent)]"
                            >
                              {period.name}
                            </Link>
                            <span className="num text-xs text-[var(--ink-3)]">
                              {period.fittingCount}/{period.investmentCount} passen
                            </span>
                          </div>
                          {period.amount > 0 ? (
                            <BudgetMeter
                              planned={period.planned}
                              budget={period.amount}
                              currency={summary.data!.currency}
                            />
                          ) : (
                            <p className="text-xs text-[var(--ink-3)]">Kein Budget gesetzt.</p>
                          )}
                        </div>
                      ))
                    )}
                    {summary.data.budget.unbudgeted.investmentCount > 0 ? (
                      <p className="border-t border-[var(--border)] pt-3 text-xs text-[var(--critical)]">
                        {formatMoneyShort(
                          summary.data.budget.unbudgeted.total,
                          summary.data.currency,
                        )}{' '}
                        in {summary.data.budget.unbudgeted.investmentCount} Investition(en) liegt
                        außerhalb aller Perioden.
                      </p>
                    ) : null}
                  </div>
                </Card>

                <Card>
                  <CardHeader title="Nach Status" subtitle="Wie weit die Vorhaben sind" />
                  <ul className="flex flex-col gap-2 px-5 pb-5">
                    {summary.data.byStatus.map((bucket) => (
                      <li key={bucket.status} className="flex items-center justify-between gap-3">
                        <StatusBadge status={bucket.status} />
                        <span className="num text-sm text-[var(--ink-2)]">
                          {bucket.investmentCount} ·{' '}
                          {formatMoneyShort(bucket.total, summary.data!.currency)}
                        </span>
                      </li>
                    ))}
                    {summary.data.byStatus.length === 0 ? (
                      <li className="text-xs text-[var(--ink-3)]">Noch nichts erfasst.</li>
                    ) : null}
                  </ul>
                </Card>
              </div>
            </div>

            <div className="grid gap-5 lg:grid-cols-[1.1fr_1fr]">
              <Card>
                <CardHeader
                  title="Geplante Kosten je Monat"
                  subtitle="Nach geplantem Startdatum"
                />
                <div className="px-5 pb-5">
                  {summary.data.byMonth.length > 0 ? (
                    <MonthlyColumns
                      data={summary.data.byMonth}
                      currency={summary.data.currency}
                    />
                  ) : (
                    <EmptyState
                      icon={<CalendarClock size={22} />}
                      title="Noch nichts terminiert"
                      description="Sobald Investitionen ein Startdatum haben, erscheinen sie hier."
                    />
                  )}
                </div>
              </Card>

              <Card>
                <CardHeader
                  title="Als Nächstes"
                  subtitle="Die fünf frühesten Termine"
                  action={
                    <Link
                      to="/jahresplan"
                      className="text-xs text-[var(--accent)] underline-offset-2 hover:underline"
                    >
                      Jahresplan
                    </Link>
                  }
                />
                <ul className="flex flex-col gap-3 px-5 pb-5">
                  {upcoming.map((investment) => (
                    <li key={investment.id} className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{investment.title}</p>
                        <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-[var(--ink-3)]">
                          {formatMonthRange(investment.plannedFrom, investment.plannedTo)}
                          {investment.groupName ? (
                            <GroupBadge
                              name={investment.groupName}
                              color={investment.groupColor ?? UNGROUPED_COLOR}
                            />
                          ) : null}
                        </p>
                      </div>
                      <span className="num shrink-0 text-sm font-medium">
                        {formatMoneyShort(investment.effectiveCost, summary.data!.currency)}
                      </span>
                    </li>
                  ))}
                  {upcoming.length === 0 ? (
                    <li className="text-xs text-[var(--ink-3)]">Noch keine Termine geplant.</li>
                  ) : null}
                </ul>
              </Card>
            </div>
          </div>
        ) : null}
      </QueryState>
    </>
  );
}
